import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Briefcase,
  CalendarDays,
  Cloud,
  Code2,
  Database,
  Facebook,
  Instagram,
  Home,
  Info,
  Linkedin,
  Link,
  LogIn,
  Mail,
  Phone,
  Settings,
  ShieldCheck,
  Sparkles,
  Twitter,
  User,
  UserPlus,
  Youtube
} from 'lucide-react';
import {
  getCompanyLogoUrl,
  getCompanyPolicyLinks,
  getTemplateCompanyContext,
  selectTemplateCompanyState
} from './TemplateCompanyUtils.js';
import { getStoredPublishedActiveTemplate } from './TemplateStorage.jsx';
import { getTemplateAccountRoute, isTemplateAuthRoute, useTemplateAuthState } from './TemplateAuthState.jsx';
import { sanitizeCss, sanitizeHtml } from './Sanitizer.jsx';
import TemplateRouterLink from './TemplateRouterLink.jsx';
import { resolveLinkHref, linkProps } from './templateLinkConfig.js';
import { resolveFooterTokens } from './footerTokens.js';
import { resolveHeaderBackground, resolveHeaderTypography, resolveHeaderNavStyle } from './headerTokens.js';
import { AnimatedRegion } from './templateAnimations.jsx';

const defaultTheme = {
  primary: '#07111f',
  secondary: '#d8c2b6',
  accent: '#0b0f16',
  background: '#e6e8eb',
  color: '#0b0f16',
  mutedColor: '#5f6670',
  borderColor: '#d5d8dd',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1120px',
  buttonRadius: '999px',
  headerHeight: '48px',
  cardShadow: '0 24px 56px rgba(24, 34, 46, 0.12)',
  footerBackground: '#e6e8eb'
};

const defaultHeader = {
  visible: true,
  sticky: true,
  overlay: true,
  height: '48px',
  backgroundColor: 'transparent',
  backgroundGradient: 'linear-gradient(180deg, rgba(8, 30, 42, .24), rgba(8, 30, 42, 0))',
  borderColor: 'transparent',
  borderWidth: '0',
  shadow: 'none',
  maxWidth: '1200px',
  logoWidth: '120px',
  logoUrl: '',
  logoAlt: 'Company logo',
  brandInitial: 'A',
  brandName: 'AVPS Tech',
  brandMaxWidth: '220px',
  brandColor: '#ffffff',
  navAlignment: 'right',
  navStyle: 'pill',
  ctaLabel: 'Contact us',
  ctaUrl: '/contactus',
  ctaIcon: 'Mail',
  ctaStyle: 'solid',
  hideCta: true,
  showSearch: false,
  searchPlaceholder: 'Search...',
  searchWidth: '150px',
  nav: [
    { label: 'Home', route: '/', icon: 'Home', visible: true },
    { label: 'Services', route: '/services', icon: 'Services', visible: true },
    { label: 'Server', route: '/server', icon: 'Cloud', visible: true },
    { label: 'About', route: '/about', icon: 'Info', visible: true },
    { label: 'Career', route: '/careers', icon: 'Briefcase', visible: true },
    { label: 'Contact Us', route: '/contactus', icon: 'Mail', visible: true },
    { label: 'Sign In', route: '/sign-in', icon: 'LogIn', visible: false },
    { label: 'Sign Up', route: '/sign-up', icon: 'UserPlus', visible: false }
  ]
};

const defaultFooter = {
  visible: true,
  newsletterTitle: 'Let us build the systems behind your next digital move.',
  newsletterPlaceholder: 'work@company.com',
  newsletterButtonLabel: 'START',
  newsletterConsentLabel: 'YES, CONTACT ME ABOUT IT SERVICES.',
  copyrightText: '(c) 2026 AVPS Tech. All rights reserved.',
  madeWithText: 'Software, cloud, servers, automation, and managed IT.',
  contact: { phone: '+1 (555) 400-1020', email: 'hello@avpstech.com', address: 'Cloud and software delivery' },
  quickLinks: [
    { label: 'Services', route: '/services', icon: 'Services', visible: true },
    { label: 'Server', route: '/server', icon: 'Cloud', visible: true },
    { label: 'Careers', route: '/careers', icon: 'Briefcase', visible: true },
    { label: 'About', route: '/about', icon: 'Info', visible: true },
    { label: 'Contact', route: '/contactus', icon: 'Mail', visible: true },
    { label: 'Sign In', route: '/sign-in', icon: 'LogIn', visible: true },
    { label: 'Sign Up', route: '/sign-up', icon: 'UserPlus', visible: true }
  ],
  socialLinks: [
    { label: 'LinkedIn', route: '', icon: 'Linkedin', platformKey: 'linkedin', visible: true },
    { label: 'YouTube', route: '', icon: 'Youtube', platformKey: 'youtube', visible: true },
    { label: 'Facebook', route: '', icon: 'Facebook', platformKey: 'facebook', visible: true },
    { label: 'X', route: '', icon: 'Twitter', platformKey: 'twitter', visible: true }
  ],
  legalLinks: [
    { label: 'Refund Policy', route: '/policy/refund_policy', visible: true },
    { label: 'Privacy Policy', route: '/policy/privacy_policy', visible: true },
    { label: 'Terms & Conditions', route: '/policy/terms_conditions', visible: true },
    { label: 'Accessibility Statement', route: '/policy/accessibility', visible: true }
  ]
};

const assetUrl = (asset, fallback = '') => {
  if (!asset) return fallback || '';
  if (typeof asset === 'string') return asset;
  return asset.displayLink || asset.storedLink || fallback || '';
};

const resolveHeaderPositionMode = (header = {}) => {
  if (['normal', 'sticky', 'fixed', 'overlay'].includes(header.positionMode)) return header.positionMode;
  if (header.overlay === true || header.headerTheme === 'transparent') return 'overlay';
  return header.sticky === false ? 'normal' : 'sticky';
};

const isTransparentHeader = (header = {}) => (
  header.headerTheme === 'transparent'
  || header.backgroundColor === 'transparent'
  || header.transparent === true
  || resolveHeaderPositionMode(header) === 'overlay'
);

const visibleItems = (items = []) => (Array.isArray(items) ? items.filter((item) => item?.visible !== false) : []);
const routeKey = (route = '') => {
  const trimmed = String(route || '').trim();
  if (!trimmed || trimmed === '/') return '/';
  return trimmed.replace(/\/+$/, '');
};
const isExternalRoute = (route = '') => /^(https?:|mailto:|tel:)/i.test(String(route || '').trim());
const pageVisibilityByRoute = (template) => {
  const routes = new Map();
  const pageMeta = template?.attributes?.pageMeta || {};
  (template?.pages || []).forEach((page) => {
    if (!page?.route) return;
    const hidden = page?.attributes?.hiddenFromNavigation === true || pageMeta?.[page.pageKey]?.hiddenFromNavigation === true;
    routes.set(routeKey(page.route), page.enabled !== false && !hidden);
  });
  return routes;
};
const isTemplateRouteVisible = (template, route) => {
  if (!route || isExternalRoute(route)) return true;
  const routes = pageVisibilityByRoute(template);
  const key = routeKey(route);
  return !routes.has(key) || routes.get(key) !== false;
};
const visibleNavigationItems = (items = [], template) => visibleItems(items).filter((item) => isTemplateRouteVisible(template, item?.route));
const waasHeaderIconMap = {
  Home,
  Services: Settings,
  Settings,
  Info,
  Briefcase,
  Mail,
  Phone,
  Calendar: CalendarDays,
  CalendarDays,
  LogIn,
  User,
  UserPlus,
  Shield: ShieldCheck,
  ShieldCheck,
  Cloud,
  Code: Code2,
  Code2,
  Database,
  Facebook,
  Instagram,
  Linkedin,
  Link,
  Twitter,
  Youtube,
  Sparkles
};
const WaasHeaderIcon = ({ name, size = 14 }) => {
  const Icon = waasHeaderIconMap[name];
  return Icon ? <Icon size={size} strokeWidth={2.3} aria-hidden="true" /> : null;
};

const socialPlatformOptions = {
  facebook: { label: 'Facebook', icon: 'Facebook', baseUrl: 'https://www.facebook.com/' },
  instagram: { label: 'Instagram', icon: 'Instagram', baseUrl: 'https://www.instagram.com/' },
  linkedin: { label: 'LinkedIn', icon: 'Linkedin', baseUrl: 'https://www.linkedin.com/company/' },
  twitter: { label: 'X', icon: 'Twitter', baseUrl: 'https://x.com/' },
  youtube: { label: 'YouTube', icon: 'Youtube', baseUrl: 'https://www.youtube.com/@' }
};

const normalizeSocialPlatform = (value = '') => {
  const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (['facebook', 'fb'].includes(normalized)) return 'facebook';
  if (['instagram', 'insta', 'ig'].includes(normalized)) return 'instagram';
  if (['linkedin', 'linkedln', 'iinkedin'].includes(normalized)) return 'linkedin';
  if (['twitter', 'x', 'xcom'].includes(normalized)) return 'twitter';
  if (['youtube', 'yt'].includes(normalized)) return 'youtube';
  return '';
};

const formatSocialRoute = (value = '', platformKey = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed === '#') return '';
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed) || /\.[a-z]{2,}(\/|$)/i.test(trimmed)) return `https://${trimmed}`;
  const baseUrl = socialPlatformOptions[platformKey]?.baseUrl;
  if (!baseUrl) return trimmed;
  return `${baseUrl}${trimmed.replace(/^@/, '').replace(/^\/+/, '')}`;
};

const socialLinksFromCompany = (company = {}) => {
  const accounts = Array.isArray(company?.socialMediaAccounts)
    ? company.socialMediaAccounts
    : Array.isArray(company?.companyDetails?.socialMediaAccounts)
      ? company.companyDetails.socialMediaAccounts
      : [];
  return accounts.map((account) => {
    const platformKey = normalizeSocialPlatform(account?.name || account?.label || account?.platform || account?.type || account?.key);
    const route = formatSocialRoute(account?.value || account?.route || account?.url || account?.href || account?.link, platformKey);
    if (!platformKey || !route) return null;
    const config = socialPlatformOptions[platformKey];
    return {
      label: account?.label || config.label,
      route,
      icon: config.icon,
      platformKey,
      visible: account?.visible !== false
    };
  }).filter(Boolean);
};

const normalizeSocialLinks = (items, company = {}) => {
  const byPlatform = new Map();
  const customLinks = [];
  const hasConfiguredList = Array.isArray(items);
  const configuredItems = visibleItems(items);
  configuredItems.forEach((item) => {
    const platformKey = normalizeSocialPlatform(item?.platformKey || item?.icon || item?.label || item?.name);
    const route = formatSocialRoute(item?.route || item?.url || item?.href || item?.value, platformKey);
    if (!route) return;
    const config = socialPlatformOptions[platformKey];
    const link = {
      ...item,
      label: item?.label || item?.name || config?.label || 'Social link',
      route,
      icon: config?.icon || item?.icon || 'Link',
      platformKey,
      visible: item?.visible !== false
    };
    if (platformKey) {
      byPlatform.set(platformKey, link);
    } else {
      customLinks.push(link);
    }
  });
  const configuredPlatformKeys = new Set(configuredItems.map((item) => normalizeSocialPlatform(item?.platformKey || item?.icon || item?.label || item?.name)).filter(Boolean));
  socialLinksFromCompany(company).forEach((item) => {
    if (hasConfiguredList && !configuredPlatformKeys.has(item.platformKey)) return;
    byPlatform.set(item.platformKey, { ...(byPlatform.get(item.platformKey) || {}), ...item });
  });
  return [...byPlatform.values(), ...customLinks];
};

const WaasSocialIcon = ({ item, size = 14 }) => <WaasHeaderIcon name={item?.icon || 'Link'} size={size} />;

// Thin wrappers delegating to the shared `templateLinkConfig` module so the
// published reader resolves links identically across all chrome surfaces.
const resolveWaasChromeHref = (source = {}, fallbackUrl = '') => resolveLinkHref(source, fallbackUrl);

const waasChromeLinkProps = (source = {}, fallbackUrl = '') => linkProps(source, fallbackUrl);

const scopeCustomCss = (css, scopeId) => sanitizeCss(css || '').split('}').map((rule) => {
  const parts = rule.split('{');
  if (parts.length < 2) return '';
  const selector = parts[0].trim();
  const body = parts.slice(1).join('{').trim();
  if (!selector || !body || selector.startsWith('@')) return '';
  const scopedSelector = selector.split(',').map((item) => {
    const trimmed = item.trim();
    return trimmed.startsWith(`#${scopeId}`) ? trimmed : `#${scopeId} ${trimmed}`;
  }).join(', ');
  return `${scopedSelector} { ${body} }`;
}).join('\n');

const resolveActiveWaasTemplate = () => (
  getStoredPublishedActiveTemplate({
    companyId: typeof window !== 'undefined' ? localStorage.getItem('CompPrimaryId') : '',
    domainType: 'WAAS'
  })
);

const useActiveWaasTemplate = () => {
  const [template, setTemplate] = useState(resolveActiveWaasTemplate);

  useEffect(() => {
    const refresh = () => setTemplate(resolveActiveWaasTemplate());
    window.addEventListener('storage', refresh);
    window.addEventListener('avps-template-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('avps-template-updated', refresh);
    };
  }, []);

  return template;
};

const themeOf = (template) => ({ ...defaultTheme, ...(template?.attributes?.theme || {}) });

export const ConfigurableWaasHeader = ({ template: providedTemplate, fallback = true }) => {
  const auth = useTemplateAuthState();
  const companyState = useSelector(selectTemplateCompanyState);
  const storedTemplate = useActiveWaasTemplate();
  const template = providedTemplate || storedTemplate;
  const theme = themeOf(template);
  const header = { ...defaultHeader, ...(template?.attributes?.header || {}) };
  if (!template && !fallback) return null;
  if (header.visible === false) return null;

  const companyContext = getTemplateCompanyContext(template?.company, companyState);
  const logo = getCompanyLogoUrl(companyContext) || assetUrl(header.logoContent, header.logoUrl);
  const nav = visibleNavigationItems(header.nav, template).filter((item) => !auth.isLoggedIn || !isTemplateAuthRoute(item));
  const ctaRoute = resolveWaasChromeHref({ link: header.ctaLink, newTab: header.ctaNewTab }, header.ctaUrl || '/contactus');
  const ctaItem = { label: header.ctaLabel || header.signInLabel, route: ctaRoute };
  const showCta = header.hideCta !== true && (!auth.isLoggedIn || !isTemplateAuthRoute(ctaItem)) && isTemplateRouteVisible(template, ctaRoute);
  const showSearch = header.showSearch === true;
  const accountRoute = getTemplateAccountRoute('WAAS');
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const navAlignment = header.navAlignment || 'center';
  const headerGrid = navAlignment === 'left' ? 'minmax(150px, auto) minmax(0, 1fr) auto' : navAlignment === 'right' ? 'minmax(0, 1fr) auto auto' : 'minmax(150px, auto) minmax(0, 1fr) auto';
  const navJustify = navAlignment === 'left' ? 'start' : navAlignment === 'right' ? 'end' : 'center';
  const customCss = scopeCustomCss(header.customCss || '', 'waas-template-header');
  const customHtml = sanitizeHtml(header.customHtml || '');
  const positionMode = resolveHeaderPositionMode(header);
  const overlayHeader = positionMode === 'overlay';
  const fixedHeader = positionMode === 'fixed';
  const transparentHeader = isTransparentHeader(header);
  const floatingHeader = overlayHeader || transparentHeader;
  const headerHeight = header.height || theme.headerHeight;
  // Task 8.3: resolved header customization (additive — unset reproduces today's look).
  // Background: keep the transparent/overlay behavior driven by isTransparentHeader,
  // and let the resolver feed the solid background color when mode === 'solid'.
  const headerBackgroundTokens = resolveHeaderBackground(theme, header);
  const headerTypography = resolveHeaderTypography(theme, header);
  const headerNavStyle = resolveHeaderNavStyle(theme, header);
  const headerBackground = headerBackgroundTokens.mode === 'solid'
    ? headerBackgroundTokens.backgroundColor
    : 'transparent';
  const navSpacing = headerNavStyle.spacing != null ? headerNavStyle.spacing : 1;
  const headerMaxWidth = header.maxWidth || theme.headerMaxWidth || '1280px';
  const brandColor = header.brandColor || '#ffffff';
  const ctaStyle = header.ctaStyle || 'solid';
  const ctaBackground = ctaStyle === 'outline' || ctaStyle === 'ghost' ? 'transparent' : theme.accent || '#0b0f16';
  const ctaColor = '#ffffff';
  const ctaBorder = ctaStyle === 'outline' ? '1px solid rgba(255,255,255,.65)' : 'none';
  const ctaShadow = floatingHeader ? 'none' : '0 12px 26px rgba(8, 13, 23, .22)';

  return (
    <>
    {/* Task 12.4: header renders through AnimatedRegion (as the <header> itself,
        no extra DOM) so attributes.header.animation plays per its trigger.
        Additive: unset animation -> null descriptor -> identical <header>. */}
    <AnimatedRegion
      as="header"
      config={header.animation}
      id="waas-template-header"
      style={{
        minHeight: headerHeight,
        background: headerBackground,
        borderBottom: `${header.borderWidth || '0'} solid ${transparentHeader ? (header.borderColor || 'transparent') : (header.borderColor || 'transparent')}`,
        position: fixedHeader ? 'fixed' : floatingHeader ? 'absolute' : positionMode === 'normal' ? 'relative' : 'sticky',
        top: positionMode === 'normal' && !floatingHeader ? 'auto' : 'env(safe-area-inset-top, 0px)',
        left: fixedHeader || floatingHeader ? 0 : undefined,
        right: fixedHeader || floatingHeader ? 0 : undefined,
        width: fixedHeader || floatingHeader ? '100%' : undefined,
        zIndex: 9999,
        boxShadow: transparentHeader ? (header.shadow || 'none') : (header.shadow || 'none')
      }}
    >
      {customCss && <style>{customCss}</style>}
      <div className="waas-template-header-inner" style={{ maxWidth: headerMaxWidth, margin: '0 auto', height: '100%', display: 'grid', gridTemplateColumns: headerGrid, alignItems: 'center', gap: header.navGap || 10, padding: '0 16px' }}>
        <TemplateRouterLink {...waasChromeLinkProps({ link: header.brandLink }, header.brandUrl || '/')} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0, maxWidth: header.brandMaxWidth || 220, color: headerTypography.color || brandColor, textDecoration: 'none', fontFamily: headerTypography.fontFamily || theme.headingFontFamily, fontSize: headerTypography.fontSize || 16, fontWeight: headerTypography.fontWeight || 800, letterSpacing: headerTypography.letterSpacing, textAlign: headerTypography.textAlign, textShadow: floatingHeader ? '0 8px 22px rgba(8, 30, 42, .34)' : 'none' }}>
          {logo ? (
            <img src={logo} alt={header.logoAlt || 'Company logo'} style={{ width: header.logoWidth || 120, height: 'auto', maxWidth: '100%', maxHeight: header.logoMaxHeight || 160, objectFit: 'contain', flex: '0 0 auto' }} />
          ) : (
            <span style={{ display: 'grid', placeItems: 'center', width: 28, height: 28, borderRadius: 999, background: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.36)', color: '#ffffff', fontFamily: theme.fontFamily, fontSize: 13, fontWeight: 900, flex: '0 0 auto' }}>{header.brandInitial || 'A'}</span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.05 }}>{header.brandName != null ? header.brandName : 'Your IT Company'}</span>
        </TemplateRouterLink>
        <nav className="waas-template-nav" style={{ justifySelf: navJustify, display: 'flex', alignItems: 'center', gap: navSpacing, minWidth: 0, maxWidth: '100%', overflow: 'hidden', background: 'transparent', border: 'none', borderRadius: 999, padding: 3, boxShadow: 'none' }}>
          {nav.map((item) => {
            const active = currentPath === item.route;
            // Use author-set fontSize/fontWeight from navStyle if present, else domain defaults.
            const navFontSize = headerNavStyle.fontSize != null ? Number(headerNavStyle.fontSize) || 10 : 10;
            const navFontWeight = headerNavStyle.fontWeight != null
              ? headerNavStyle.fontWeight
              : (active ? 800 : 700);
            return (
              <TemplateRouterLink key={`${item.label}-${item.route}`} {...linkProps(item.link, item.route)} style={{ display: 'inline-flex', alignItems: 'center', gap: 0, padding: '7px 11px', borderRadius: 999, color: active ? '#ffffff' : (headerNavStyle.linkColor || '#ffffff'), background: active ? (theme.accent || '#0b0f16') : 'transparent', textDecoration: 'none', fontFamily: headerNavStyle.fontFamily, fontWeight: navFontWeight, fontSize: navFontSize, lineHeight: 1, letterSpacing: 0, textTransform: 'uppercase', whiteSpace: 'nowrap', border: '1px solid transparent' }}>
                {item.label}
              </TemplateRouterLink>
            );
          })}
        </nav>
        {(showSearch || showCta || auth.isLoggedIn) && <div className="waas-template-header-cta" style={{ justifySelf: 'end', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          {showSearch && (
            <input
              aria-label={header.searchPlaceholder || 'Search'}
              placeholder={header.searchPlaceholder || 'Search'}
              style={{ width: header.searchWidth || 150, minHeight: 28, borderRadius: 6, border: `1px solid ${header.borderColor || theme.borderColor}`, padding: '0 9px', fontSize: 11, outline: 0 }}
            />
          )}
          {showCta && (
            <TemplateRouterLink {...waasChromeLinkProps({ link: header.ctaLink, newTab: header.ctaNewTab }, header.ctaUrl || '/contactus')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 32, borderRadius: 999, background: ctaBackground, color: ctaColor, border: ctaBorder, textDecoration: 'none', padding: '0 15px', fontWeight: 800, fontSize: 11, lineHeight: 1, textTransform: 'uppercase', whiteSpace: 'nowrap', boxShadow: ctaShadow }}>
              <WaasHeaderIcon name={header.ctaIcon} size={15} />
              {header.ctaLabel || header.signInLabel || 'Book a consultation'}
            </TemplateRouterLink>
          )}
          {auth.isLoggedIn && (
            <TemplateRouterLink to={accountRoute} title={auth.displayName || 'My Profile'} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 30, borderRadius: 999, background: '#ffffff', color: '#17202c', border: '1px solid rgba(23,32,44,.16)', textDecoration: 'none', padding: '0 10px', fontWeight: 800, fontSize: 10, lineHeight: 1, textTransform: 'uppercase', whiteSpace: 'nowrap', maxWidth: 138, overflow: 'hidden', boxShadow: floatingHeader ? '0 8px 18px rgba(8, 13, 23, .14)' : 'none' }}>
              {auth.avatarUrl ? (
                <img src={auth.avatarUrl} alt="" style={{ width: 18, height: 18, borderRadius: 999, objectFit: 'cover', flex: '0 0 auto' }} />
              ) : (
                <WaasHeaderIcon name="User" size={14} />
              )}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{auth.displayName || 'Profile'}</span>
            </TemplateRouterLink>
          )}
        </div>}
      </div>
      {customHtml && <div dangerouslySetInnerHTML={{ __html: customHtml }} />}
      <style>{`
        .waas-template-header-inner a,
        .waas-template-header-inner nav {
          min-width: 0;
        }
        .waas-template-nav a svg,
        .waas-template-header-cta a svg {
          flex: 0 0 auto;
        }
        @media (max-width: 780px) {
          .waas-template-nav {
            display: none !important;
          }
          .waas-template-header-inner {
            grid-template-columns: minmax(150px, 1fr) auto !important;
          }
        }
        @media (max-width: 640px) {
          .waas-template-nav,
          .waas-template-header-cta {
            display: none !important;
          }
          .waas-template-header-inner {
            grid-template-columns: minmax(0, 1fr) !important;
            padding: 0 16px !important;
          }
        }
      `}</style>
    </AnimatedRegion>
    {fixedHeader && !transparentHeader && <div aria-hidden="true" style={{ height: headerHeight }} />}
    </>
  );
};

export const ConfigurableWaasFooter = ({ template: providedTemplate, fallback = true }) => {
  const auth = useTemplateAuthState();
  const companyState = useSelector(selectTemplateCompanyState);
  const storedTemplate = useActiveWaasTemplate();
  const template = providedTemplate || storedTemplate;
  const theme = themeOf(template);
  const footer = { ...defaultFooter, ...(template?.attributes?.footer || {}) };
  if (!template && !fallback) return null;
  if (footer.visible === false) return null;

  const footerSource = template?.attributes?.footer || {};
  // Bug A fix: colors/fonts/radius cascade through resolveFooterTokens
  // (footer override > Template_Theme token > legacy default). defaultFooter is
  // kept for structural defaults only (link lists, labels, layout).
  const tokens = resolveFooterTokens(theme, footerSource);
  const quickLinks = visibleNavigationItems(footerSource.quickLinks || footerSource.nav || footer.quickLinks || footer.nav, template).filter((item) => !auth.isLoggedIn || !isTemplateAuthRoute(item));
  const companyContext = getTemplateCompanyContext(template?.company, companyState);
  const legalLinks = getCompanyPolicyLinks(companyContext);
  const socialLinks = normalizeSocialLinks(footer.socialLinks, companyContext);
  const companyLinks = quickLinks.filter((item) => /about|career|contact/i.test(item.label || '')).slice(0, 4);
  const serviceLinks = quickLinks.filter((item) => /service|server/i.test(item.label || '')).slice(0, 4);
  const policyLinks = legalLinks.slice(0, 4);
  const extraColumnCount = (serviceLinks.length ? 1 : 0) + (policyLinks.length ? 1 : 0);
  // Task 9.3: additive footer customization (typography / columns / editable links).
  // Every field is read from the persisted footer source; an UNSET field keeps
  // today's look (non-regression). fontFamily/colors already flow via tokens.
  const footerTypography = footerSource.typography && typeof footerSource.typography === 'object' ? footerSource.typography : {};
  const footerTypographyStyle = {
    ...(footerTypography.textAlign ? { textAlign: footerTypography.textAlign } : {}),
    ...(footerTypography.fontSize != null && footerTypography.fontSize !== '' ? { fontSize: footerTypography.fontSize } : {}),
    ...(footerTypography.fontWeight != null && footerTypography.fontWeight !== '' ? { fontWeight: footerTypography.fontWeight } : {}),
    ...(footerTypography.letterSpacing != null && footerTypography.letterSpacing !== '' ? { letterSpacing: footerTypography.letterSpacing } : {})
  };
  // The footer's child elements (headings / spans / links) carry their OWN explicit
  // inline font-size / weight / text-align, which override the root-level typography
  // style above — so footer font/spacing/alignment edits appeared to "do nothing".
  // Emit a scoped !important style (only for the keys the author actually set) so the
  // typography reaches the children. font-family / letter-spacing / text-align apply to
  // every footer element; font-size / weight apply to the text elements only, so section
  // headings keep their relative prominence. Unset keys emit nothing (non-regression).
  const footerCssLen = (v) => {
    if (v == null || v === '') return '';
    if (typeof v === 'number') return `${v}px`;
    const s = String(v).trim();
    return /^-?\d+(\.\d+)?$/.test(s) ? `${s}px` : s;
  };
  const footerAllRule = [
    footerTypography.fontFamily ? `font-family:${footerTypography.fontFamily} !important;` : '',
    (footerTypography.letterSpacing != null && footerTypography.letterSpacing !== '') ? `letter-spacing:${footerCssLen(footerTypography.letterSpacing)} !important;` : '',
    footerTypography.textAlign ? `text-align:${footerTypography.textAlign} !important;` : '',
    // Footer text color must reach the headings / links too (they carry their own
    // explicit colors), so apply it to every footer text node when the author sets it.
    footerTypography.textColor ? `color:${footerTypography.textColor} !important;` : ''
  ].join('');
  const footerTextRule = [
    (footerTypography.fontSize != null && footerTypography.fontSize !== '') ? `font-size:${footerCssLen(footerTypography.fontSize)} !important;` : '',
    (footerTypography.fontWeight != null && footerTypography.fontWeight !== '') ? `font-weight:${footerTypography.fontWeight} !important;` : ''
  ].join('');
  const footerTypographyCss = [
    footerAllRule ? `#waas-template-footer, #waas-template-footer * { ${footerAllRule} }` : '',
    footerTextRule ? `#waas-template-footer p, #waas-template-footer a, #waas-template-footer span, #waas-template-footer li, #waas-template-footer div { ${footerTextRule} }` : ''
  ].filter(Boolean).join('\n');
  const editableFooterLinks = Array.isArray(footerSource.links)
    ? footerSource.links.filter((item) => item && item.visible !== false)
    : [];
  const footerColumnsOverride = Number(footerSource.columns);
  const hasFooterColumnsOverride = Number.isFinite(footerColumnsOverride) && footerColumnsOverride > 0;
  const computedFooterGridColumns = extraColumnCount === 2
    ? '1.1fr .8fr .85fr .85fr'
    : extraColumnCount === 1
      ? '1.1fr .85fr .95fr'
      : '1.1fr .85fr';
  const footerGridColumns = hasFooterColumnsOverride
    ? `repeat(${footerColumnsOverride}, minmax(0, 1fr))`
    : computedFooterGridColumns;
  const customCss = scopeCustomCss(footer.customCss || '', 'waas-template-footer');
  const customHtml = sanitizeHtml(footer.customHtml || '');

  return (
    <AnimatedRegion as="footer" config={footer.animation} id="waas-template-footer" style={{ background: tokens.backgroundColor, color: tokens.textColor, fontFamily: tokens.fontFamily, borderTop: `1px solid ${tokens.borderColor}`, ...(footerSource.minHeight ? { minHeight: footerSource.minHeight } : {}), ...footerTypographyStyle }}>
      {customCss && <style>{customCss}</style>}
      {footerTypographyCss && <style>{footerTypographyCss}</style>}
      <div style={{ maxWidth: theme.pageWidth, margin: '0 auto', padding: '52px 30px 30px' }}>
        <div id="waas-template-footer-grid" style={{ display: 'grid', gridTemplateColumns: footerGridColumns, gap: 40, alignItems: 'start' }}>
          <div>
            <h3 style={{ margin: '0 0 22px', fontFamily: theme.headingFontFamily, fontWeight: 800, color: tokens.headingColor, fontSize: 22 }}>{template?.attributes?.header?.brandName || 'AVPS Tech'}</h3>
            <div style={{ display: 'grid', gap: 10, color: tokens.textColor, fontSize: 12, lineHeight: 1.5 }}>
              <span>{footer.contact?.phone || '+1 (555) 400-1020'}</span>
              <span>{footer.contact?.email || 'hello@avpstech.com'}</span>
              <span>{footer.contact?.address || footer.madeWithText}</span>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 16 }}>
            <strong style={{ color: tokens.headingColor, fontSize: 13 }}>Company Info</strong>
            {(companyLinks.length ? companyLinks : quickLinks.slice(0, 4)).map((item) => (
              <TemplateRouterLink key={item.label} {...waasChromeLinkProps(item, item.route)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: tokens.linkColor, textDecoration: 'none', fontSize: 12 }}>{item.label}</TemplateRouterLink>
            ))}
          </div>
          {serviceLinks.length > 0 && (
            <div style={{ display: 'grid', gap: 16 }}>
              <strong style={{ color: tokens.headingColor, fontSize: 13 }}>{footer.servicesTitle || 'Services'}</strong>
              {serviceLinks.map((item) => (
                <TemplateRouterLink key={item.label} {...waasChromeLinkProps(item, item.route)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: tokens.linkColor, textDecoration: 'none', fontSize: 12 }}>{item.label}</TemplateRouterLink>
              ))}
            </div>
          )}
          {policyLinks.length > 0 && (
            <div style={{ display: 'grid', gap: 16 }}>
              <strong style={{ color: tokens.headingColor, fontSize: 13 }}>{footer.legalTitle || 'Policies'}</strong>
              {policyLinks.map((item) => (
                <TemplateRouterLink key={item.label} {...waasChromeLinkProps(item, item.route)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: tokens.linkColor, textDecoration: 'none', fontSize: 12 }}>{item.label}</TemplateRouterLink>
              ))}
            </div>
          )}
          {editableFooterLinks.length > 0 && (
            <div style={{ display: 'grid', gap: 16 }}>
              <strong style={{ color: tokens.headingColor, fontSize: 13 }}>{footer.linksTitle || 'Links'}</strong>
              {editableFooterLinks.map((item, index) => (
                <TemplateRouterLink key={`${item.label || 'link'}-${index}`} {...linkProps(item.link, item.route)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: tokens.linkColor, textDecoration: 'none', fontSize: 12 }}>{item.label}</TemplateRouterLink>
              ))}
            </div>
          )}
        </div>
        <div style={{ borderTop: `1px solid ${tokens.borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, marginTop: 44, paddingTop: 24, fontSize: 11 }}>
          <div style={{ display: 'flex', gap: 14 }}>
            {socialLinks.map((item) => (
              <TemplateRouterLink key={`${item.label}-${item.route}`} {...waasChromeLinkProps(item, item.route)} title={item.ariaLabel || item.label} aria-label={item.ariaLabel || item.label} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 999, background: '#ffffff', border: `1px solid ${tokens.borderColor}`, color: tokens.linkColor, textDecoration: 'none', fontWeight: 800 }}>
                <WaasSocialIcon item={item} size={14} />
              </TemplateRouterLink>
            ))}
          </div>
          <span>{footer.copyrightText}</span>
        </div>
      </div>
      {customHtml && <div dangerouslySetInnerHTML={{ __html: customHtml }} />}
      <style>{`
        @media (max-width: 900px) {
          #waas-template-footer-grid {
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 32px !important;
          }
        }
      `}</style>
    </AnimatedRegion>
  );
};

export const WaasFallbackHome = () => (
  <main style={{ minHeight: '70vh', background: defaultTheme.background, color: defaultTheme.color, fontFamily: defaultTheme.fontFamily }}>
    <section style={{ minHeight: 640, padding: '70px 24px 150px', textAlign: 'center', color: '#ffffff', backgroundImage: 'linear-gradient(180deg, rgba(36,100,118,.18), rgba(11,15,22,.22)), url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=85&fit=crop&auto=format)', backgroundSize: 'cover', backgroundPosition: 'center bottom' }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <p style={{ display: 'inline-flex', border: '1px solid rgba(255,255,255,.55)', borderRadius: 999, background: 'rgba(255,255,255,.12)', padding: '9px 17px', margin: '0 0 26px', fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase' }}>AVPS Tech</p>
        <h1 style={{ fontFamily: defaultTheme.headingFontFamily, fontWeight: 300, fontSize: 'clamp(50px, 7.8vw, 112px)', lineHeight: .96, margin: 0, color: '#ffffff' }}>
          Designing digital systems that shape operations
        </h1>
        <p style={{ maxWidth: 690, margin: '34px auto 0', color: 'rgba(255,255,255,.9)', fontSize: 18, lineHeight: 1.55 }}>
          Architecture, cloud, server operations, and software delivery shaped into calm digital experiences.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', marginTop: 34 }}>
          <TemplateRouterLink to="/services" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 42, borderRadius: 999, background: defaultTheme.accent, color: '#ffffff', textDecoration: 'none', padding: '0 24px', fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Explore services</TemplateRouterLink>
          <TemplateRouterLink to="/server" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 42, borderRadius: 999, border: '1px solid rgba(255,255,255,.65)', color: '#ffffff', textDecoration: 'none', padding: '0 24px', fontWeight: 800, fontSize: 11, textTransform: 'uppercase' }}>Server infrastructure</TemplateRouterLink>
        </div>
      </div>
    </section>
  </main>
);
